import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Button,
} from 'antd';
import {
  SaveOutlined,
  EditOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import LadaNumero from 'components/LadaNumero';
import AvatarUploader from 'components/AvatarUploader';
import { onError } from 'utils/handlers';
import { getCurrentUser, patchCurrentUser } from 'api/usuarios';
import { discartNullValues } from 'utils/formatValues';
import NumericInput from 'components/NumericInput';

export const permissionConfig = {
  permissionModel: 'usuario',
  catalogsModels: ['lada'],
};

const CuentaDeUsuario = () => {
  const history = useHistory();
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    await getCurrentUser();
    setLoading(false);
  };

  useEffect(() => {
    form.setFieldsValue(discartNullValues({
      ...user,
      lada: user?.lada?.id,
      justificacion: user?.justificacion?.descripcion,
      tipo_de_usuario: user?.es_externo ? 'Externo' : 'Funcionario',
      funcion_del_usuario_externo: user?.funcion_del_usuario_externo?.descripcion,
    }));
  }, [user]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await getCurrentUser();
      if (mounted) {
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const onCancel = async () => {
    form.resetFields();
    setDisabled(true);
    await fetchData();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const props = [
        'first_name',
        'last_name',
        'second_last_name',
        'lada',
        'numero_de_celular',
        'email_alternativo',
        'foto',
      ];
      const values = form.getFieldsValue(props);
      const saved = await patchCurrentUser(values);
      if (saved) {
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    required: [{ required: true }],
    email_alternativo: [{ type: 'email' }],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          title="Información de Usuario"
          extra={(
            <FormSubmitCustomControls
              controls={disabled
                ? [
                  {
                    onClick: () => history.push('/dashboard'),
                    icon: <ArrowLeftOutlined />,
                    text: 'Regresar',
                  },
                  {
                    onClick: () => setDisabled(false),
                    icon: <EditOutlined />,
                    text: 'Editar',
                  },
                ] : [
                  {
                    onClick: onCancel,
                    icon: <ArrowLeftOutlined />,
                    text: 'Cancelar',
                  },
                  {
                    onClick: onFinish,
                    icon: <SaveOutlined />,
                    text: 'Guardar',
                  },
                ]}
            />
          )}
          bordered={false}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8} lg={6}>
                <AvatarUploader
                  form={form}
                  disabled={disabled}
                  imageURL={form.getFieldValue('foto')}
                />
              </Col>

              <Col xs={24} sm={24} md={16} lg={18}>
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Form.Item
                      name="first_name"
                      rules={rules.required}
                      label="Nombre(s)"
                      hasFeedback
                    >
                      <Input allowClear disabled={disabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Form.Item
                      name="last_name"
                      rules={rules.required}
                      label="Apellido paterno"
                      hasFeedback
                    >
                      <Input allowClear disabled={disabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Form.Item
                      name="second_last_name"
                      rules={rules.second_last_name}
                      label="Apellido materno"
                    >
                      <Input allowClear disabled={disabled} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={user?.es_externo ? 8 : 12}>
                    <Form.Item
                      name="email"
                      rules={rules.email}
                      label="Correo electrónico"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={12} xl={user?.es_externo ? 8 : 12}>
                    <Form.Item
                      name="email_alternativo"
                      rules={rules.email_alternativo}
                      label="Correo alternativo"
                    >
                      <Input allowClear disabled={disabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={12} xl={user?.es_externo ? 8 : 12}>
                    <Form.Item
                      name="tipo_de_usuario"
                      label="Tipo de usuario"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>

                  <LadaNumero
                    form={form}
                    names={{
                      lada: 'lada',
                      numero_de_telefono: 'numero_de_celular',
                    }}
                    disabled={disabled}
                    celular
                    breakPoints={{
                      md: 24,
                      lg: 12,
                      xl: user?.es_externo ? 8 : 12,
                      xxl: 12,
                    }}
                  />

                  {user?.es_externo && (
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        name="funcion_del_usuario_externo"
                        label="Función"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                  )}
                  {user?.es_externo && (
                    <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                      <Form.Item
                        name="justificacion"
                        label="Justificación"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="numero_de_empleado"
                      label="Número de Empleado"
                    />
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Col>
            </Row>
          </Form>
        </Card>
      </Spin>
    </Row>
  );
};

export default CuentaDeUsuario;
